import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import DashboardLayout from "../components/DashboardLayout";
import { auth, db } from "../firebase";
import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  getDoc,
  query,
  where,
  serverTimestamp,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import * as XLSX from "xlsx";
import {
  formatDate,
  formatDateForInput,
  parseDateString,
  getReportDate,
  getISOWeekStartEnd,
  getWeeksInYear,
  getISOWeekNumber,
} from "../utils/dateUtils";
import { createNotification } from "../utils/notifications";
import { createUserAccount, generateEmail } from "../utils/createUserAccount";

function Instructors() {
  const { t } = useTranslation();

  // State management
  const [instructors, setInstructors] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [instructorToDelete, setInstructorToDelete] = useState(null);
  const [viewingInstructor, setViewingInstructor] = useState(null);
  const [editingInstructor, setEditingInstructor] = useState(null);
  const [isWeeklyReportModalOpen, setIsWeeklyReportModalOpen] = useState(false);
  const [weeklyReportYear, setWeeklyReportYear] = useState(new Date().getFullYear());
  const [weeklyReportWeek, setWeeklyReportWeek] = useState(() =>
    getISOWeekNumber(new Date())
  );
  const [weeklyReportLoading, setWeeklyReportLoading] = useState(false);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phonePrefix: "+383", // Default phone prefix
    phoneNumber: "",
    email: "",
    personalNumber: "",
    username: "",
    password: "",
    birthday: "",
    gender: "",
    experience: "",
    instructorType: "",
    categoryLicenses: [],
    vehicleType: "",
    address: "",
    notes: "",
    status: "active",
    licenseExpiryDate: "",
    healthCertificateExpiryDate: "",
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Search and filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("name-az");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [selectedYear, setSelectedYear] = useState("all");
  const [selectedMonth, setSelectedMonth] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 30;

  // Toast notification
  const [toast, setToast] = useState({ show: false, message: "", type: "" });

  // User role state
  const [userRole, setUserRole] = useState(null);
  const [userEntityId, setUserEntityId] = useState(null);
  const [assignedInstructorId, setAssignedInstructorId] = useState(null);

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type: "" }), 3000);
  };

  // Check if user is admin
  const isAdmin = userRole === "admin";
  const isCandidate = userRole === "candidate";

  // Helper function to translate instructor type
  const translateInstructorType = (type) => {
    if (!type) return "";
    const typeMap = {
      "Theory Instructor": t("instructors.types.theory"),
      "Practical Instructor": t("instructors.types.practical"),
      Both: t("instructors.types.both"),
    };
    return typeMap[type] || type;
  };

  // Helper function to get full phone number
  const getFullPhoneNumber = (instructor) => {
    const prefix = instructor.phonePrefix || "+383";
    const number = instructor.phoneNumber || "";
    // If phoneNumber already includes prefix (old format), return as is
    if (number.startsWith("+")) {
      return number.replace(/\s/g, "");
    }
    // Combine prefix and number
    const fullNumber = `${prefix}${number.replace(/\s/g, "")}`;
    return fullNumber;
  };

  // Helper function to open WhatsApp chat
  const openWhatsApp = (instructor) => {
    const fullNumber = getFullPhoneNumber(instructor);
    // Remove + and spaces
    let whatsappNumber = fullNumber.replace(/\+|\s/g, "");
    // Remove leading 0 after country code (e.g., 383044986926 -> 38344986926)
    if (whatsappNumber.startsWith("383") && whatsappNumber[3] === "0") {
      whatsappNumber = "383" + whatsappNumber.substring(4);
    }
    window.open(`https://wa.me/${whatsappNumber}`, "_blank");
  };

  // Helper function to open Viber chat
  const openViber = (instructor) => {
    const fullNumber = getFullPhoneNumber(instructor);
    // Remove + and spaces
    let viberNumber = fullNumber.replace(/\+|\s/g, "");
    // Remove leading 0 after country code (e.g., 383044986926 -> 38344986926)
    if (viberNumber.startsWith("383") && viberNumber[3] === "0") {
      viberNumber = "383" + viberNumber.substring(4);
    }
    window.open(`viber://chat?number=${viberNumber}`, "_blank");
  };

  // Helper function to get status color for row indicator
  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "bg-green-500";
      case "inactive":
        return "bg-gray-500";
      case "completed":
        return "bg-blue-500";
      case "paused":
        return "bg-yellow-500";
      case "failed":
        return "bg-red-500";
      default:
        return "bg-gray-500";
    }
  };

  // Helper function to count active candidates for an instructor
  const getActiveCandidatesCount = (instructorId) => {
    if (!instructorId) return 0;
    const instructor = instructors.find((i) => i.id === instructorId);
    if (!instructor) return 0;

    const instructorFullName = `${instructor.firstName} ${instructor.lastName}`;

    return candidates.filter((candidate) => {
      const matchesById = candidate.assignedInstructorId === instructorId;
      const matchesByName = candidate.assignedInstructor === instructorFullName;
      const isActive =
        candidate.status !== "completed" && candidate.status !== "failed";

      return (matchesById || matchesByName) && isActive;
    }).length;
  };

  // Helper function to count completed candidates for an instructor
  const getCompletedCandidatesCount = (instructorId) => {
    if (!instructorId) return 0;
    const instructor = instructors.find((i) => i.id === instructorId);
    if (!instructor) return 0;

    const instructorFullName = `${instructor.firstName} ${instructor.lastName}`;

    return candidates.filter((candidate) => {
      const matchesById = candidate.assignedInstructorId === instructorId;
      const matchesByName = candidate.assignedInstructor === instructorFullName;

      return (matchesById || matchesByName) && candidate.status === "completed";
    }).length;
  };

  // Helper function to get total hours for an instructor (sum of all assigned candidates)
  const getInstructorTotalHours = (instructorId) => {
    if (!instructorId) return 0;
    const instructor = instructors.find((i) => i.id === instructorId);
    if (!instructor) return 0;

    const instructorFullName = `${instructor.firstName} ${instructor.lastName}`;

    return candidates
      .filter((candidate) => {
        const matchesById = candidate.assignedInstructorId === instructorId;
        const matchesByName =
          candidate.assignedInstructor === instructorFullName;
        return matchesById || matchesByName;
      })
      .reduce((total, candidate) => {
        const totalHours = parseFloat(candidate.totalHours) || 0;
        const bonusHours = parseFloat(candidate.bonusHours) || 0;
        return total + totalHours + bonusHours;
      }, 0);
  };

  // Helper function to get completed hours for an instructor
  const getInstructorCompletedHours = (instructorId) => {
    if (!instructorId) return 0;
    const instructor = instructors.find((i) => i.id === instructorId);
    if (!instructor) return 0;

    const instructorFullName = `${instructor.firstName} ${instructor.lastName}`;

    return candidates
      .filter((candidate) => {
        const matchesById = candidate.assignedInstructorId === instructorId;
        const matchesByName =
          candidate.assignedInstructor === instructorFullName;
        return matchesById || matchesByName;
      })
      .reduce((total, candidate) => {
        const completedHours = parseFloat(candidate.completedHours) || 0;
        const completedBonusHours =
          parseFloat(candidate.completedBonusHours) || 0;
        return total + completedHours + completedBonusHours;
      }, 0);
  };

  // Helper function to get total bonus hours for an instructor
  const getInstructorBonusHours = (instructorId) => {
    if (!instructorId) return 0;
    const instructor = instructors.find((i) => i.id === instructorId);
    if (!instructor) return 0;

    const instructorFullName = `${instructor.firstName} ${instructor.lastName}`;

    return candidates
      .filter((candidate) => {
        const matchesById = candidate.assignedInstructorId === instructorId;
        const matchesByName =
          candidate.assignedInstructor === instructorFullName;
        return matchesById || matchesByName;
      })
      .reduce((total, candidate) => {
        const bonusHours = parseFloat(candidate.bonusHours) || 0;
        return total + bonusHours;
      }, 0);
  };

  // Helper function to get completed bonus hours for an instructor
  const getInstructorCompletedBonusHours = (instructorId) => {
    if (!instructorId) return 0;
    const instructor = instructors.find((i) => i.id === instructorId);
    if (!instructor) return 0;

    const instructorFullName = `${instructor.firstName} ${instructor.lastName}`;

    return candidates
      .filter((candidate) => {
        const matchesById = candidate.assignedInstructorId === instructorId;
        const matchesByName =
          candidate.assignedInstructor === instructorFullName;
        return matchesById || matchesByName;
      })
      .reduce((total, candidate) => {
        const completedBonusHours =
          parseFloat(candidate.completedBonusHours) || 0;
        return total + completedBonusHours;
      }, 0);
  };

  // Helper function to get regular hours (totalHours without bonus) for an instructor
  const getInstructorRegularHours = (instructorId) => {
    if (!instructorId) return 0;
    const instructor = instructors.find((i) => i.id === instructorId);
    if (!instructor) return 0;

    const instructorFullName = `${instructor.firstName} ${instructor.lastName}`;

    return candidates
      .filter((candidate) => {
        const matchesById = candidate.assignedInstructorId === instructorId;
        const matchesByName =
          candidate.assignedInstructor === instructorFullName;
        return matchesById || matchesByName;
      })
      .reduce((total, candidate) => {
        const totalHours = parseFloat(candidate.totalHours) || 0;
        return total + totalHours;
      }, 0);
  };

  // Helper function to get completed regular hours (completedHours without completedBonusHours) for an instructor
  const getInstructorCompletedRegularHours = (instructorId) => {
    if (!instructorId) return 0;
    const instructor = instructors.find((i) => i.id === instructorId);
    if (!instructor) return 0;

    const instructorFullName = `${instructor.firstName} ${instructor.lastName}`;

    return candidates
      .filter((candidate) => {
        const matchesById = candidate.assignedInstructorId === instructorId;
        const matchesByName =
          candidate.assignedInstructor === instructorFullName;
        return matchesById || matchesByName;
      })
      .reduce((total, candidate) => {
        const completedHours = parseFloat(candidate.completedHours) || 0;
        return total + completedHours;
      }, 0);
  };

  // Helper function to format hours (remove .0 for whole numbers)
  const formatHours = (hours) => {
    const num = parseFloat(hours) || 0;
    // Always return whole number
    return Math.round(num).toString();
  };

  // Fetch instructors from Firestore
  const fetchInstructors = useCallback(
    async (showLoading = true) => {
      try {
        if (showLoading) {
          setLoading(true);
        }
        const querySnapshot = await getDocs(collection(db, "instructors"));
        const instructorsData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setInstructors(instructorsData);
      } catch (error) {
        console.error("Error fetching instructors:", error);
        // Only show error toast if this is the initial load, not a refresh after save
        if (showLoading) {
          showToast(t("instructors.toast.fetchError"), "error");
        }
      } finally {
        if (showLoading) {
          setLoading(false);
        }
      }
    },
    [t]
  );

  // Fetch candidates from Firestore
  const fetchCandidates = useCallback(async () => {
    try {
      const querySnapshot = await getDocs(collection(db, "candidates"));
      const candidatesData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setCandidates(candidatesData);
    } catch (error) {
      console.error("Error fetching candidates:", error);
    }
  }, []);

  // Check for expiring licenses and health certificates (daily notifications 1 week before expiry)
  const checkExpiringDocuments = useCallback(async () => {
    try {
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      const today = new Date(now);

      // Get all admin users
      const usersSnapshot = await getDocs(collection(db, "users"));
      const adminUsers = [];
      const instructorUsers = new Map(); // Map instructorId -> authUid

      usersSnapshot.docs.forEach((userDoc) => {
        const userData = userDoc.data();
        if (userData.role === "admin") {
          adminUsers.push(userDoc.id);
        } else if (userData.role === "instructor" && userData.entityId) {
          instructorUsers.set(userData.entityId, userDoc.id);
        }
      });

      // Helper function to calculate days until expiry
      const getDaysUntilExpiry = (expiryDate) => {
        const expiry = new Date(expiryDate);
        expiry.setHours(0, 0, 0, 0);
        const diffTime = expiry - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
      };

      // Helper function to check if notification exists for today
      const notificationExistsForToday = async (type, entityId) => {
        const todayStart = new Date(today);
        todayStart.setHours(0, 0, 0, 0);
        const todayEnd = new Date(today);
        todayEnd.setHours(23, 59, 59, 999);

        const existingNotifications = await getDocs(
          query(
            collection(db, "notifications"),
            where("type", "==", type),
            where("entityId", "==", entityId)
          )
        );

        // Check if any notification was created today
        for (const doc of existingNotifications.docs) {
          const notifData = doc.data();
          if (notifData.createdAt) {
            const createdAt = notifData.createdAt.toDate
              ? notifData.createdAt.toDate()
              : new Date(notifData.createdAt);
            if (createdAt >= todayStart && createdAt <= todayEnd) {
              return true;
            }
          }
        }
        return false;
      };

      // Check each instructor for expiring documents
      for (const instructor of instructors) {
        const instructorName = `${instructor.firstName} ${instructor.lastName}`;

        // Check license expiry
        if (instructor.licenseExpiryDate) {
          const daysUntilExpiry = getDaysUntilExpiry(
            instructor.licenseExpiryDate
          );

          // Check if license expires within the next 7 days (including today if it expires today)
          if (daysUntilExpiry >= 0 && daysUntilExpiry <= 7) {
            // Check if notification already exists for today
            const existsToday = await notificationExistsForToday(
              "instructor_license_expiring",
              instructor.id
            );

            if (!existsToday) {
              // Create notification for admin
              await createNotification(
                "instructor_license_expiring",
                "instructor",
                instructor.id,
                instructorName,
                {
                  expiryDate: instructor.licenseExpiryDate,
                  documentType: "license",
                  daysUntilExpiry: daysUntilExpiry,
                }
              );

              // Create notification for instructor if they have an auth account
              const instructorAuthUid =
                instructor.authUid || instructorUsers.get(instructor.id);
              if (instructorAuthUid) {
                await createNotification(
                  "instructor_license_expiring",
                  "instructor",
                  instructor.id,
                  instructorName,
                  {
                    expiryDate: instructor.licenseExpiryDate,
                    documentType: "license",
                    daysUntilExpiry: daysUntilExpiry,
                    recipientId: instructorAuthUid, // Target specific instructor
                  }
                );
              }
            }
          }
        }

        // Check health certificate expiry
        if (instructor.healthCertificateExpiryDate) {
          const daysUntilExpiry = getDaysUntilExpiry(
            instructor.healthCertificateExpiryDate
          );

          // Check if health certificate expires within the next 7 days (including today if it expires today)
          if (daysUntilExpiry >= 0 && daysUntilExpiry <= 7) {
            // Check if notification already exists for today
            const existsToday = await notificationExistsForToday(
              "instructor_health_certificate_expiring",
              instructor.id
            );

            if (!existsToday) {
              // Create notification for admin
              await createNotification(
                "instructor_health_certificate_expiring",
                "instructor",
                instructor.id,
                instructorName,
                {
                  expiryDate: instructor.healthCertificateExpiryDate,
                  documentType: "health_certificate",
                  daysUntilExpiry: daysUntilExpiry,
                }
              );

              // Create notification for instructor if they have an auth account
              const instructorAuthUid =
                instructor.authUid || instructorUsers.get(instructor.id);
              if (instructorAuthUid) {
                await createNotification(
                  "instructor_health_certificate_expiring",
                  "instructor",
                  instructor.id,
                  instructorName,
                  {
                    expiryDate: instructor.healthCertificateExpiryDate,
                    documentType: "health_certificate",
                    daysUntilExpiry: daysUntilExpiry,
                    recipientId: instructorAuthUid, // Target specific instructor
                  }
                );
              }
            }
          }
        }
      }
    } catch (error) {
      console.error("Error checking expiring documents:", error);
      // Don't show error to user - this is a background check
    }
  }, [instructors]);

  useEffect(() => {
    fetchInstructors();
    fetchCandidates();
  }, [fetchInstructors, fetchCandidates]);

  // Check for expiring documents when instructors are loaded
  useEffect(() => {
    if (instructors.length > 0) {
      checkExpiringDocuments();
    }
  }, [instructors, checkExpiringDocuments]);

  // Fetch user role and entityId
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const userDoc = await getDoc(doc(db, "users", user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setUserRole(userData.role || null);
            setUserEntityId(userData.entityId || null);
          }
        } catch (error) {
          console.error("Error fetching user role:", error);
        }
      } else {
        setUserRole(null);
        setUserEntityId(null);
      }
    });

    return () => unsubscribe();
  }, []);

  // Fetch candidate's assigned instructor ID for candidates
  useEffect(() => {
    const fetchCandidateInstructor = async () => {
      if (userRole === "candidate" && userEntityId) {
        try {
          const candidateDoc = await getDoc(
            doc(db, "candidates", userEntityId)
          );
          if (candidateDoc.exists()) {
            const candidateData = candidateDoc.data();
            setAssignedInstructorId(candidateData.assignedInstructorId || null);
          }
        } catch (error) {
          console.error("Error fetching candidate instructor:", error);
        }
      }
    };

    fetchCandidateInstructor();
  }, [userRole, userEntityId]);

  // Validation
  const validateField = (name, value) => {
    let error = "";

    const requiredFields = [
      "firstName",
      "lastName",
      "phoneNumber",
      "personalNumber",
      "username",
      "password",
      "birthday",
      "gender",
      "experience",
      "categoryLicenses",
      "status",
    ];

    if (requiredFields.includes(name) && !value) {
      error = t("instructors.validation.required");
    }

    // Password validation
    if (name === "password" && value && value.length < 6) {
      error = t("instructors.validation.passwordLength");
    }

    // Email validation (optional field)
    if (
      name === "email" &&
      value &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
    ) {
      error = t("instructors.validation.emailInvalid");
    }

    // Phone prefix validation
    if (name === "phonePrefix" && value && !/^\+?\d{1,4}$/.test(value)) {
      error = t("instructors.validation.phonePrefixInvalid");
    }

    // Phone number validation (without prefix)
    if (name === "phoneNumber" && value && !/^[\d\s\-()]+$/.test(value)) {
      error = t("instructors.validation.phoneInvalid");
    }

    // Combined phone validation: if phoneNumber exists, phonePrefix should exist
    if (name === "phoneNumber" && value && !formData.phonePrefix) {
      error = t("instructors.validation.phonePrefixRequired");
    }

    // Combined phone validation: if phonePrefix exists, phoneNumber should exist
    if (name === "phonePrefix" && value && !formData.phoneNumber) {
      error = t("instructors.validation.phoneNumberRequired");
    }

    // Personal number validation (10 digits)
    if (
      name === "personalNumber" &&
      value &&
      value.replace(/\D/g, "").length !== 10
    ) {
      error = t("instructors.validation.personalNumberLength");
    }

    // Username validation
    if (name === "username" && value && value.length < 3) {
      error = t("instructors.validation.usernameLength");
    }

    // Experience validation (must be a number)
    if (name === "experience" && value && isNaN(value)) {
      error = t("instructors.validation.experienceInvalid");
    }

    // Category licenses validation (array)
    if (
      name === "categoryLicenses" &&
      Array.isArray(value) &&
      value.length === 0
    ) {
      error = t("instructors.validation.required");
    }

    return error;
  };

  const handleChange = (e) => {
    const { name, value, checked } = e.target;

    if (name === "categoryLicenses") {
      const currentLicenses = formData.categoryLicenses || [];
      const updatedLicenses = checked
        ? [...currentLicenses, value]
        : currentLicenses.filter((license) => license !== value);

      setFormData((prev) => ({
        ...prev,
        categoryLicenses: updatedLicenses,
      }));

      const error = validateField(name, updatedLicenses);
      setErrors((prev) => ({ ...prev, [name]: error }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
      const error = validateField(name, value);
      setErrors((prev) => ({ ...prev, [name]: error }));
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
  };

  const validateForm = () => {
    const newErrors = {};
    const requiredFields = [
      "firstName",
      "lastName",
      "phonePrefix",
      "phoneNumber",
      "personalNumber",
      "username",
      "birthday",
      "gender",
      "experience",
      "categoryLicenses",
      "status",
    ];

    // Password is required only if:
    // 1. Creating a new instructor (no editingInstructor), OR
    // 2. Editing an instructor that doesn't have an authUid
    const passwordRequired = !editingInstructor || !editingInstructor.authUid;
    if (passwordRequired) {
      requiredFields.push("password");
    }

    requiredFields.forEach((field) => {
      const error = validateField(field, formData[field]);
      if (error) newErrors[field] = error;
    });

    setErrors(newErrors);
    setTouched(
      requiredFields.reduce((acc, field) => ({ ...acc, [field]: true }), {})
    );

    return Object.keys(newErrors).length === 0;
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      showToast(t("instructors.toast.fillRequired"), "error");
      return;
    }

    try {
      if (editingInstructor) {
        // Update existing instructor
        const instructorRef = doc(db, "instructors", editingInstructor.id);
        await updateDoc(instructorRef, {
          ...formData,
          updatedAt: serverTimestamp(),
        });

        // Create auth account if it doesn't exist yet
        if (!editingInstructor.authUid) {
          const email = generateEmail(formData.username, formData.email);
          console.log("Creating auth account for instructor:", {
            instructorId: editingInstructor.id,
            email: email,
            hasPassword: !!formData.password,
            passwordLength: formData.password?.length
          });
          if (email && formData.password) {
            // Get current admin user info before creating new user
            const currentAdmin = auth.currentUser;
            const adminEmail = currentAdmin?.email;

            const authResult = await createUserAccount(
              email,
              formData.password,
              "instructor",
              {
                firstName: formData.firstName,
                lastName: formData.lastName,
                phoneNumber: formData.phoneNumber,
              },
              editingInstructor.id,
              adminEmail,
              null // We don't have admin password, so session won't be restored automatically
            );

            if (authResult.success) {
              // Update instructor document with auth UID
              await updateDoc(instructorRef, {
                authUid: authResult.uid,
                email: email,
              });
              showToast(
                "Authentication account created successfully! Instructor can now login.",
                "success"
              );
            } else {
              // If email already exists, try to find the existing user in Firestore
              if (authResult.error && authResult.error.includes("already exists")) {
                try {
                  const usersQuery = query(
                    collection(db, "users"),
                    where("email", "==", email)
                  );
                  const usersSnapshot = await getDocs(usersQuery);
                  
                  if (!usersSnapshot.empty) {
                    // Found existing user, link it to the instructor
                    const existingUser = usersSnapshot.docs[0];
                    await updateDoc(instructorRef, {
                      authUid: existingUser.id,
                      email: email,
                    });
                    console.log("Linked existing auth account to instructor");
                    showToast(
                      "Linked existing authentication account to instructor.",
                      "success"
                    );
                  } else {
                    // Email exists in Auth but no user document - this is a problem
                    console.error("Email exists in Firebase Auth but no user document found");
                    showToast(
                      `Warning: Auth account exists but couldn't link it. Please contact administrator.`,
                      "error"
                    );
                  }
                } catch (linkError) {
                  console.error("Error linking existing account:", linkError);
                  showToast(
                    `Warning: Could not create or link auth account: ${authResult.error}`,
                    "error"
                  );
                }
              } else {
                // Other error
                console.error("Failed to create auth account:", authResult.error);
                showToast(
                  `Warning: Could not create auth account: ${authResult.error}`,
                  "error"
                );
              }
            }
          } else if (!formData.password) {
            // Password is required to create auth account
            showToast(
              "Password is required to create authentication account. Please provide a password.",
              "error"
            );
          }
        }

        // Show success message immediately after instructor is updated
        showToast(t("instructors.toast.updated"), "success");

        // Close modal immediately
        handleCloseModal();

        // Try to create notification (non-critical, don't fail if this errors)
        try {
          const instructorName = `${formData.firstName} ${formData.lastName}`;
          await createNotification(
            "instructor_updated",
            "instructor",
            editingInstructor.id,
            instructorName
          );
        } catch (notificationError) {
          console.warn("Failed to create notification:", notificationError);
          // Don't show error to user - notification is not critical
        }

        // Refresh instructors list (non-critical, don't fail if this errors)
        // Pass false to showLoading to avoid showing loading state during refresh
        try {
          await fetchInstructors(false);
        } catch (fetchError) {
          console.warn("Failed to refresh instructors list:", fetchError);
          // Don't show error to user - we can refresh manually
        }
      } else {
        // Create new instructor
        const docRef = await addDoc(collection(db, "instructors"), {
          ...formData,
          createdAt: serverTimestamp(),
        });

        // Create Firebase Auth account for the instructor
        const email = generateEmail(formData.username, formData.email);
        if (email && formData.password) {
          // Get current admin user info before creating new user
          const currentAdmin = auth.currentUser;
          const adminEmail = currentAdmin?.email;

          const authResult = await createUserAccount(
            email,
            formData.password,
            "instructor",
            {
              firstName: formData.firstName,
              lastName: formData.lastName,
              phoneNumber: formData.phoneNumber,
            },
            docRef.id,
            adminEmail,
            null // We don't have admin password, so session won't be restored automatically
          );

          if (authResult.success) {
            // Update instructor document with auth UID
            await updateDoc(docRef, {
              authUid: authResult.uid,
              email: email,
            });
          } else {
            console.warn("Failed to create auth account:", authResult.error);
            // Still show success for instructor creation, but log the auth error
          }
        }

        // Show success message immediately after instructor is created
        showToast(t("instructors.toast.created"), "success");

        // Close modal immediately
        handleCloseModal();

        // Try to create notification (non-critical, don't fail if this errors)
        try {
          const instructorName = `${formData.firstName} ${formData.lastName}`;
          await createNotification(
            "instructor_added",
            "instructor",
            docRef.id,
            instructorName
          );
        } catch (notificationError) {
          console.warn("Failed to create notification:", notificationError);
          // Don't show error to user - notification is not critical
        }

        // Refresh instructors list (non-critical, don't fail if this errors)
        // Pass false to showLoading to avoid showing loading state during refresh
        try {
          await fetchInstructors(false);
        } catch (fetchError) {
          console.warn("Failed to refresh instructors list:", fetchError);
          // Don't show error to user - we can refresh manually
        }
      }
    } catch (error) {
      console.error("Error saving instructor:", error);
      showToast(t("instructors.toast.saveError"), "error");
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!instructorToDelete) return;

    try {
      // Check if instructor has any assigned candidates
      const candidatesQuery = query(
        collection(db, "candidates"),
        where("assignedInstructorId", "==", instructorToDelete.id)
      );
      const candidatesSnapshot = await getDocs(candidatesQuery);

      if (!candidatesSnapshot.empty) {
        // Instructor has assigned candidates, prevent deletion
        showToast(
          t("instructors.toast.hasAssignedCandidates", {
            count: candidatesSnapshot.size,
          }),
          "error"
        );
        setIsDeleteModalOpen(false);
        setInstructorToDelete(null);
        return;
      }

      // No assigned candidates, safe to delete
      const instructorName = `${instructorToDelete.firstName} ${instructorToDelete.lastName}`;
      await deleteDoc(doc(db, "instructors", instructorToDelete.id));
      await createNotification(
        "instructor_deleted",
        "instructor",
        instructorToDelete.id,
        instructorName
      );
      showToast(t("instructors.toast.deleted"), "success");
      fetchInstructors();
      setIsDeleteModalOpen(false);
      setInstructorToDelete(null);
    } catch (error) {
      console.error("Error deleting instructor:", error);
      showToast(t("instructors.toast.deleteError"), "error");
    }
  };

  const handleView = (instructor) => {
    setViewingInstructor(instructor);
    setIsViewModalOpen(true);
  };

  const handleEdit = (instructor) => {
    setEditingInstructor(instructor);

    // Handle phone number: extract prefix if exists, otherwise use default
    let phonePrefix = instructor.phonePrefix || "+383";
    let phoneNumber = instructor.phoneNumber || "";

    // If phoneNumber contains prefix (old format), extract it
    if (phoneNumber && phoneNumber.startsWith("+")) {
      const match = phoneNumber.match(/^(\+\d{1,4})\s*(.+)$/);
      if (match) {
        phonePrefix = match[1];
        phoneNumber = match[2];
      }
    }

    setFormData({
      firstName: instructor.firstName || "",
      lastName: instructor.lastName || "",
      phonePrefix: phonePrefix,
      phoneNumber: phoneNumber,
      email: instructor.email || "",
      personalNumber: instructor.personalNumber || "",
      username: instructor.username || "",
      password: instructor.password || "",
      birthday: instructor.birthday || "",
      gender: instructor.gender || "",
      experience: instructor.experience || "",
      instructorType: instructor.instructorType || "",
      categoryLicenses: instructor.categoryLicenses || [],
      vehicleType: instructor.vehicleType || "",
      address: instructor.address || "",
      notes: instructor.notes || "",
      status: instructor.status || "active",
      licenseExpiryDate: instructor.licenseExpiryDate || "",
      healthCertificateExpiryDate: instructor.healthCertificateExpiryDate || "",
    });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingInstructor(null);
    setFormData({
      firstName: "",
      lastName: "",
      phonePrefix: "+383",
      phoneNumber: "",
      email: "",
      personalNumber: "",
      username: "",
      password: "",
      birthday: "",
      gender: "",
      experience: "",
      instructorType: "",
      categoryLicenses: [],
      vehicleType: "",
      address: "",
      notes: "",
      status: "active",
      licenseExpiryDate: "",
      healthCertificateExpiryDate: "",
    });
    setErrors({});
    setTouched({});
  };

  const handleClearFilters = () => {
    setSearchQuery("");
    setSortBy("name-az");
    setFilterStatus("");
    setFilterType("");
    setFilterCategory("");
    setFilterDateFrom("");
    setFilterDateTo("");
    setSelectedYear("all");
    setSelectedMonth("all");
  };

  // Export to Excel
  const handleExportToExcel = () => {
    const exportData = filteredAndSortedInstructors.map((instructor) => ({
      "First Name": instructor.firstName,
      "Last Name": instructor.lastName,
      Username: instructor.username,
      "Phone Number": instructor.phoneNumber,
      Email: instructor.email || "N/A",
      "Personal Number": instructor.personalNumber,
      Birthday: formatDate(instructor.birthday),
      Gender: instructor.gender,
      Experience: instructor.experience,
      "Instructor Type": instructor.instructorType || "N/A",
      "Category Licenses": instructor.categoryLicenses?.join(", ") || "N/A",
      "Vehicle Type": instructor.vehicleType || "N/A",
      Address: instructor.address || "N/A",
      Status: instructor.status,
      Notes: instructor.notes || "",
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Instructors");
    XLSX.writeFile(
      wb,
      `instructors_${new Date().toISOString().split("T")[0]}.xlsx`
    );
  };

  // Admin email: weekly report only includes reports created by admin (not by instructor)
  const WEEKLY_REPORT_ADMIN_EMAIL = "burimiautoshkolla@gmail.com";

  // Generate weekly report: one aggregated table (total hours per candidate, normal/bonus breakdown)
  // Only reports created by admin (createdByEmail) are included.
  const handleGenerateWeeklyReport = async () => {
    if (!viewingInstructor) return;
    setWeeklyReportLoading(true);
    try {
      const { start, end } = getISOWeekStartEnd(weeklyReportYear, weeklyReportWeek);
      const snapshot = await getDocs(collection(db, "reports"));
      const allReports = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      let weekReports = allReports.filter((r) => {
        if (r.instructorId !== viewingInstructor.id) return false;
        const rd = getReportDate(r);
        return rd && rd >= start && rd <= end;
      });
      // Only include reports created by the admin (instructor-added reports are excluded)
      weekReports = weekReports.filter(
        (r) => (r.createdByEmail || "").toLowerCase() === WEEKLY_REPORT_ADMIN_EMAIL.toLowerCase()
      );

      if (weekReports.length === 0) {
        showToast(t("instructors.weeklyReport.noReportsForWeek"), "error");
        setWeeklyReportLoading(false);
        return;
      }

      const dateFromStr = formatDate(start);
      const dateToStr = formatDate(end);
      const periodLabel = t("instructors.weeklyReport.period", {
        from: dateFromStr,
        to: dateToStr,
      });
      const instructorName =
        `${viewingInstructor.firstName || ""} ${viewingInstructor.lastName || ""}`.trim() ||
        viewingInstructor.id;
      const instructorPhone =
        (viewingInstructor.phonePrefix || "") + (viewingInstructor.phoneNumber || "");

      // Aggregate hours per candidate: { candidateId: { name, total, normal, bonus } }
      const agg = {};
      weekReports.forEach((report) => {
        (report.candidateHours || []).forEach((ch) => {
          const id = ch.candidateId || "unknown";
          const name = ch.candidateName || "";
          const hours = parseFloat(ch.hours) || 0;
          const type = ch.hoursType || "normal";
          if (!agg[id]) {
            const cand = candidates.find((c) => c.id === id);
            const candidateIdDisplay = cand?.candidateId || "";
            agg[id] = {
              name: name + (candidateIdDisplay ? ` (${candidateIdDisplay})` : ""),
              total: 0,
              normal: 0,
              bonus: 0,
            };
          }
          agg[id].total += hours;
          if (type === "normal") agg[id].normal += hours;
          else if (type === "bonus") agg[id].bonus += hours;
          // normalAndBonus: count in total only (no split stored)
        });
      });

      const rows = Object.entries(agg)
        .map(([id, v]) => ({ id, ...v }))
        .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      const grandTotal = rows.reduce((s, r) => s + r.total, 0);
      const grandNormal = rows.reduce((s, r) => s + r.normal, 0);
      const grandBonus = rows.reduce((s, r) => s + r.bonus, 0);

      const testTypeLabel = (testType) =>
        testType === "praktik"
          ? t("candidates.form.practicalTestStatus") || "Practical Test"
          : t("candidates.form.theoryTestStatus") || "Theory Test";

      const hoursTableRows = rows
        .map(
          (r, idx) => `
        <tr style="${idx % 2 === 0 ? "background-color: #ffffff;" : "background-color: #f5f5f5;"}">
          <td style="padding: 12px 16px; border-bottom: 1px solid #000000; font-size: 14px; color: #000000;">${r.name || "-"}</td>
          <td style="padding: 12px 16px; border-bottom: 1px solid #000000; text-align: right; font-weight: 600; font-size: 14px; color: #000000;">${Math.round(r.total)}h</td>
          <td style="padding: 12px 16px; border-bottom: 1px solid #000000; text-align: right; font-size: 14px; color: #000000;">${Math.round(r.normal)}h</td>
          <td style="padding: 12px 16px; border-bottom: 1px solid #000000; text-align: right; font-size: 14px; color: #000000;">${Math.round(r.bonus)}h</td>
        </tr>`
        )
        .join("");

      const allTests = weekReports.flatMap((r) => (r.testResults || []).map((t) => ({ ...t })));
      const testsTableRows = allTests
        .map((test, idx) => {
          const cand = candidates.find((c) => c.id === test.candidateId);
          const name = test.candidateName || (cand ? `${cand.firstName || ""} ${cand.lastName || ""}`.trim() : "-");
          const idDisplay = cand?.candidateId || "";
          return `
        <tr style="${idx % 2 === 0 ? "background-color: #ffffff;" : "background-color: #f5f5f5;"}">
          <td style="padding: 12px 16px; border-bottom: 1px solid #000000; font-size: 14px; color: #000000;">${name}${idDisplay ? ` (${idDisplay})` : ""}</td>
          <td style="padding: 12px 16px; border-bottom: 1px solid #000000; font-size: 14px; color: #000000;">${testTypeLabel(test.testType || "teorik")}</td>
          <td style="padding: 12px 16px; border-bottom: 1px solid #000000; text-align: center; font-size: 13px;">
            <span style="display: inline-block; padding: 6px 14px; border-radius: 4px; font-weight: 700; background-color: #000000; color: #ffffff;">${test.passed ? t("reports.form.passed") : t("reports.form.failed")}</span>
          </td>
        </tr>`;
        })
        .join("");

      const hoursTableHtml = `
        <div style="margin-top: 24px;">
          <h3 style="font-size: 16px; font-weight: 700; margin-bottom: 14px; color: #000000;">
            ${t("reports.form.candidateHours") || "Candidate hours"}
          </h3>
          <table style="width: 100%; border-collapse: collapse;">
            <thead>
              <tr>
                <th style="padding: 14px 16px; text-align: left; font-weight: 700; font-size: 13px; color: #000000; text-transform: uppercase; letter-spacing: 0.8px; border-bottom: 1px solid #000000;">${t("reports.print.candidate") || "Candidate"}</th>
                <th style="padding: 14px 16px; text-align: right; font-weight: 700; font-size: 13px; color: #000000; text-transform: uppercase; letter-spacing: 0.8px; border-bottom: 1px solid #000000;">${t("instructors.weeklyReport.totalHours") || "Total hours"}</th>
                <th style="padding: 14px 16px; text-align: right; font-weight: 700; font-size: 13px; color: #000000; text-transform: uppercase; letter-spacing: 0.8px; border-bottom: 1px solid #000000;">${t("instructors.weeklyReport.normal") || "Normal"}</th>
                <th style="padding: 14px 16px; text-align: right; font-weight: 700; font-size: 13px; color: #000000; text-transform: uppercase; letter-spacing: 0.8px; border-bottom: 1px solid #000000;">${t("instructors.weeklyReport.bonus") || "Bonus"}</th>
              </tr>
            </thead>
            <tbody>
              ${hoursTableRows}
              <tr style="background-color: #f5f5f5;">
                <td style="padding: 14px 16px; border-top: 2px solid #000000; font-weight: 700; font-size: 14px; color: #000000;">${t("reports.form.total") || "Total"}</td>
                <td style="padding: 14px 16px; border-top: 2px solid #000000; text-align: right; font-weight: 700; font-size: 14px; color: #000000;">${Math.round(grandTotal)}h</td>
                <td style="padding: 14px 16px; border-top: 2px solid #000000; text-align: right; font-weight: 700; font-size: 14px; color: #000000;">${Math.round(grandNormal)}h</td>
                <td style="padding: 14px 16px; border-top: 2px solid #000000; text-align: right; font-weight: 700; font-size: 14px; color: #000000;">${Math.round(grandBonus)}h</td>
              </tr>
            </tbody>
          </table>
        </div>`;

      const testsTableHtml =
        allTests.length > 0
          ? `
        <div style="margin-top: 32px;">
          <h3 style="font-size: 16px; font-weight: 700; margin-bottom: 14px; color: #000000;">
            ${t("reports.form.testResultsSummary") || "Test results"}
          </h3>
          <table style="width: 100%; border-collapse: collapse;">
            <thead>
              <tr>
                <th style="padding: 14px 16px; text-align: left; font-weight: 700; font-size: 13px; color: #000000; text-transform: uppercase; letter-spacing: 0.8px; border-bottom: 1px solid #000000;">${t("reports.print.candidate") || "Candidate"}</th>
                <th style="padding: 14px 16px; text-align: left; font-weight: 700; font-size: 13px; color: #000000; text-transform: uppercase; letter-spacing: 0.8px; border-bottom: 1px solid #000000;">${t("reports.print.testType") || "Test type"}</th>
                <th style="padding: 14px 16px; text-align: center; font-weight: 700; font-size: 13px; color: #000000; text-transform: uppercase; letter-spacing: 0.8px; border-bottom: 1px solid #000000;">${t("reports.print.result") || "Result"}</th>
              </tr>
            </thead>
            <tbody>${testsTableRows}</tbody>
          </table>
        </div>`
          : "";

      // Notes from each admin report in the week (date + notes)
      const reportsWithNotes = weekReports
        .filter((r) => r.notes && String(r.notes).trim())
        .sort((a, b) => (getReportDate(a) || 0) - (getReportDate(b) || 0));
      const notesBlocks = reportsWithNotes
        .map(
          (r) => `
        <div style="margin-bottom: 16px;">
          <div style="font-weight: 700; font-size: 13px; color: #000000; margin-bottom: 4px;">${formatDate(r.date)}</div>
          <div style="white-space: pre-wrap; font-size: 14px; line-height: 1.6; color: #000000; padding: 12px; background-color: #f9f9f9; border-left: 3px solid #000000;">${String(r.notes).trim()}</div>
        </div>`
        )
        .join("");
      const notesSectionHtml =
        notesBlocks.length > 0
          ? `
        <div style="margin-top: 32px;">
          <h3 style="font-size: 16px; font-weight: 700; margin-bottom: 14px; color: #000000;">
            ${t("instructors.weeklyReport.notesTitle") || "Notes"}
          </h3>
          ${notesBlocks}
        </div>`
          : "";

      const printWindow = window.open("", "_blank");
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>${t("reports.print.weeklyReport") || "Weekly Report"} – ${dateFromStr} / ${dateToStr}</title>
            <style>
              @media print { body { margin: 0; padding: 20px; } .no-print { display: none; } @page { margin: 1.5cm; } }
              * { box-sizing: border-box; }
              body { font-family: Arial, Helvetica, sans-serif; padding: 30px; color: #000000; max-width: 210mm; margin: 0 auto; background: #ffffff; }
              .header-top { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 25px; }
              .period-label { font-size: 16px; font-weight: 700; color: #000000; padding-top: 8px; }
              .logo-container { height: 70px; }
              .logo-container img { height: 100%; width: auto; object-fit: contain; max-width: 200px; }
              .instructor-info { margin-bottom: 24px; padding: 20px; background-color: #f5f5f5; border-left: 4px solid #000000; }
              .instructor-info h3 { font-size: 14px; font-weight: 700; margin-bottom: 16px; color: #000000; text-transform: uppercase; letter-spacing: 0.8px; }
              .instructor-info .label { font-weight: 700; display: inline-block; min-width: 120px; color: #000000; }
              table { width: 100%; border-collapse: collapse; margin-top: 10px; }
            </style>
          </head>
          <body>
            <div class="header-top">
              <div class="period-label">${periodLabel}</div>
              <div class="logo-container">
                <img src="${window.location.origin}/logoBlack.png" alt="Logo" onerror="this.style.display='none'" />
              </div>
            </div>
            <div class="instructor-info">
              <h3>${t("reports.print.instructorInfo") || "Instructor info"}:</h3>
              <p><span class="label">${t("reports.print.name") || "Name"}:</span> ${instructorName}</p>
              <p><span class="label">${t("reports.print.phone") || "Phone"}:</span> ${instructorPhone}</p>
            </div>
            <div class="divider-line" style="border-bottom: 2px solid #000000; margin-bottom: 24px;"></div>
            ${hoursTableHtml}
            ${testsTableHtml}
            ${notesSectionHtml}
          </body>
        </html>
      `);
      printWindow.document.close();
      setTimeout(() => printWindow.print(), 250);
      setIsWeeklyReportModalOpen(false);
    } catch (err) {
      console.error("Error generating weekly report:", err);
      showToast(t("instructors.toast.fetchError"), "error");
    } finally {
      setWeeklyReportLoading(false);
    }
  };

  // Get available years from instructors (based on createdAt)
  const availableYears = useMemo(() => {
    const years = new Set();
    instructors.forEach((instructor) => {
      const createdAt = instructor.createdAt
        ? new Date(instructor.createdAt.seconds * 1000)
        : null;
      if (createdAt && !isNaN(createdAt.getTime())) {
        years.add(createdAt.getFullYear());
      }
    });
    return Array.from(years).sort((a, b) => b - a); // Sort descending (newest first)
  }, [instructors]);

  // Get available months for selected year
  const availableMonths = useMemo(() => {
    if (selectedYear === "all") return [];

    const months = new Set();
    instructors.forEach((instructor) => {
      const createdAt = instructor.createdAt
        ? new Date(instructor.createdAt.seconds * 1000)
        : null;
      if (
        createdAt &&
        !isNaN(createdAt.getTime()) &&
        createdAt.getFullYear() === parseInt(selectedYear)
      ) {
        months.add(createdAt.getMonth() + 1); // Month is 0-indexed, so add 1
      }
    });
    return Array.from(months).sort((a, b) => a - b); // Sort ascending (1-12)
  }, [instructors, selectedYear]);

  // Reset month when year changes to "all"
  useEffect(() => {
    if (selectedYear === "all") {
      setSelectedMonth("all");
    }
  }, [selectedYear]);

  // Calculate stats
  const stats = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const thisYear = new Date(now.getFullYear(), 0, 1);

    // Filter instructors by selected year and month (based on createdAt)
    const filteredInstructors = instructors.filter((instructor) => {
      const createdAt = instructor.createdAt
        ? new Date(instructor.createdAt.seconds * 1000)
        : null;
      if (!createdAt || isNaN(createdAt.getTime())) return false;

      // Filter by year
      if (selectedYear !== "all") {
        if (createdAt.getFullYear() !== parseInt(selectedYear)) return false;
      }

      // Filter by month (only if year is selected)
      if (selectedYear !== "all" && selectedMonth !== "all") {
        if (createdAt.getMonth() + 1 !== parseInt(selectedMonth)) return false;
      }

      return true;
    });

    // Determine the month range for stats (based on selected month, or current month)
    let selectedMonthStart, selectedMonthEnd;
    if (selectedYear !== "all" && selectedMonth !== "all") {
      // Use selected month
      selectedMonthStart = new Date(
        parseInt(selectedYear),
        parseInt(selectedMonth) - 1,
        1
      );
      selectedMonthEnd = new Date(
        parseInt(selectedYear),
        parseInt(selectedMonth) - 1,
        new Date(parseInt(selectedYear), parseInt(selectedMonth), 0).getDate(),
        23,
        59,
        59
      );
    } else {
      // Use current month
      selectedMonthStart = thisMonth;
      selectedMonthEnd = now;
    }

    // Determine the year range for stats (always the whole year, not just selected month)
    let selectedYearStart, selectedYearEnd;
    if (selectedYear === "all") {
      selectedYearStart = thisYear;
      selectedYearEnd = new Date(now.getFullYear(), 11, 31, 23, 59, 59);
    } else {
      // Always use the whole year
      selectedYearStart = new Date(parseInt(selectedYear), 0, 1);
      selectedYearEnd = new Date(parseInt(selectedYear), 11, 31, 23, 59, 59);
    }

    let todayTotal = 0;
    let todayMale = 0;
    let todayFemale = 0;
    let monthTotal = 0;
    let monthMale = 0;
    let monthFemale = 0;
    let yearTotal = 0;
    let yearMale = 0;
    let yearFemale = 0;
    let allTimeTotal = filteredInstructors.length;
    let allTimeMale = 0;
    let allTimeFemale = 0;

    filteredInstructors.forEach((instructor) => {
      const createdAt = instructor.createdAt
        ? new Date(instructor.createdAt.seconds * 1000)
        : null;

      // Count by gender for all time
      if (instructor.gender === "male") {
        allTimeMale++;
      } else if (instructor.gender === "female") {
        allTimeFemale++;
      }

      if (createdAt && !isNaN(createdAt.getTime())) {
        // Today
        if (createdAt >= today) {
          todayTotal++;
          if (instructor.gender === "male") todayMale++;
          else if (instructor.gender === "female") todayFemale++;
        }

        // Selected Month (or current month if no month selected)
        if (createdAt >= selectedMonthStart && createdAt <= selectedMonthEnd) {
          monthTotal++;
          if (instructor.gender === "male") monthMale++;
          else if (instructor.gender === "female") monthFemale++;
        }

        // Selected Year / This Year (always whole year)
        if (createdAt >= selectedYearStart && createdAt <= selectedYearEnd) {
          yearTotal++;
          if (instructor.gender === "male") yearMale++;
          else if (instructor.gender === "female") yearFemale++;
        }
      }
    });

    return {
      today: {
        total: todayTotal,
        male: todayMale,
        female: todayFemale,
      },
      month: {
        total: monthTotal,
        male: monthMale,
        female: monthFemale,
      },
      year: {
        total: yearTotal,
        male: yearMale,
        female: yearFemale,
      },
      allTime: {
        total: allTimeTotal,
        male: allTimeMale,
        female: allTimeFemale,
      },
    };
  }, [instructors, selectedYear, selectedMonth]);

  // Filtered and sorted instructors
  const filteredAndSortedInstructors = useMemo(() => {
    let filtered = [...instructors];

    // For candidates, only show their assigned instructor
    if (userRole === "candidate" && assignedInstructorId) {
      filtered = filtered.filter(
        (instructor) => instructor.id === assignedInstructorId
      );
      return filtered; // Return early for candidates - no need for other filters
    }

    // Search filter (only for admin/instructor)
    if (searchQuery) {
      const searchLower = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (instructor) =>
          instructor.firstName?.toLowerCase().includes(searchLower) ||
          instructor.lastName?.toLowerCase().includes(searchLower) ||
          instructor.username?.toLowerCase().includes(searchLower) ||
          instructor.phoneNumber?.toLowerCase().includes(searchLower) ||
          instructor.email?.toLowerCase().includes(searchLower) ||
          instructor.personalNumber?.toLowerCase().includes(searchLower)
      );
    }

    // Status filter
    if (filterStatus) {
      filtered = filtered.filter(
        (instructor) => instructor.status === filterStatus
      );
    }

    // Instructor type filter
    if (filterType) {
      filtered = filtered.filter(
        (instructor) => instructor.instructorType === filterType
      );
    }

    // Category filter
    if (filterCategory) {
      filtered = filtered.filter((instructor) => {
        const licenses = instructor.categoryLicenses || [];
        return licenses.includes(filterCategory);
      });
    }

    // Date range filter (based on createdAt)
    if (filterDateFrom || filterDateTo) {
      filtered = filtered.filter((instructor) => {
        const createdAt = instructor.createdAt
          ? new Date(instructor.createdAt.seconds * 1000)
          : null;

        if (!createdAt || isNaN(createdAt.getTime())) return false;

        // Set time to start of day for comparison
        const instructorDate = new Date(
          createdAt.getFullYear(),
          createdAt.getMonth(),
          createdAt.getDate()
        );

        if (filterDateFrom) {
          const fromDate = parseDateString(filterDateFrom);
          if (fromDate) {
            fromDate.setHours(0, 0, 0, 0);
            if (instructorDate < fromDate) return false;
          }
        }

        if (filterDateTo) {
          const toDate = parseDateString(filterDateTo);
          if (toDate) {
            toDate.setHours(23, 59, 59, 999);
            if (instructorDate > toDate) return false;
          }
        }

        return true;
      });
    }

    // Sorting
    filtered.sort((a, b) => {
      if (sortBy === "name-az") {
        return a.firstName.localeCompare(b.firstName);
      } else if (sortBy === "name-za") {
        return b.firstName.localeCompare(a.firstName);
      } else if (sortBy === "experience-high") {
        return parseInt(b.experience || 0) - parseInt(a.experience || 0);
      } else if (sortBy === "experience-low") {
        return parseInt(a.experience || 0) - parseInt(b.experience || 0);
      }
      return 0;
    });

    return filtered;
  }, [
    instructors,
    searchQuery,
    filterStatus,
    filterType,
    filterCategory,
    filterDateFrom,
    filterDateTo,
    sortBy,
    userRole,
    assignedInstructorId,
  ]);

  // Pagination calculations
  const totalPages = Math.ceil(
    filteredAndSortedInstructors.length / itemsPerPage
  );
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedInstructors = filteredAndSortedInstructors.slice(
    startIndex,
    endIndex
  );

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [
    searchQuery,
    filterStatus,
    filterType,
    filterCategory,
    filterDateFrom,
    filterDateTo,
    sortBy,
  ]);

  // Get unique instructor types for filter
  const uniqueInstructorTypes = useMemo(() => {
    const types = instructors
      .map((instructor) => instructor.instructorType)
      .filter((type) => type);
    return [...new Set(types)];
  }, [instructors]);

  // Get unique categories for filter
  const uniqueCategories = useMemo(() => {
    const categories = new Set();
    instructors.forEach((instructor) => {
      const licenses = instructor.categoryLicenses || [];
      licenses.forEach((license) => categories.add(license));
    });
    return Array.from(categories).sort();
  }, [instructors]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* Toast Notification */}
      {toast.show && (
        <div className="fixed top-4 right-4 z-50 animate-fade-in-down">
          <div
            className={`px-6 py-3 rounded-lg shadow-lg ${
              toast.type === "success"
                ? "bg-green-500 text-white"
                : "bg-red-500 text-white"
            }`}
          >
            {toast.message}
          </div>
        </div>
      )}

      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {t("instructors.title")}
            </h2>
            <p className="text-gray-600">{t("instructors.subtitle")}</p>
          </div>

          {!isCandidate && (
            <div className="flex flex-wrap gap-3">
              <button
                onClick={handleExportToExcel}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium flex items-center space-x-2"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <span>{t("instructors.exportToExcel")}</span>
              </button>
              {isAdmin && (
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors font-medium flex items-center space-x-2"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                  <span>{t("instructors.addInstructor")}</span>
                </button>
              )}
            </div>
          )}
        </div>

        {/* Year and Month Selectors - Hidden for candidates */}
        {!isCandidate && (
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center gap-3 flex-wrap">
              <label className="text-sm font-medium text-gray-700">
                {t("instructors.stats.filterByYear")}:
              </label>
              <select
                value={selectedYear}
                onChange={(e) => {
                  setSelectedYear(e.target.value);
                  setSelectedMonth("all"); // Reset month when year changes
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black text-sm bg-white"
              >
                <option value="all">{t("instructors.stats.allYears")}</option>
                {availableYears.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>

              {selectedYear !== "all" && (
                <>
                  <label className="text-sm font-medium text-gray-700 ml-2">
                    {t("instructors.stats.filterByMonth")}:
                  </label>
                  <select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black text-sm bg-white"
                  >
                    <option value="all">
                      {t("instructors.stats.allMonths")}
                    </option>
                    {availableMonths.map((month) => (
                      <option key={month} value={month}>
                        {t(`instructors.stats.months.${month}`)}
                      </option>
                    ))}
                  </select>
                </>
              )}
            </div>
          </div>
        )}

        {/* Stats Cards - Hidden for candidates */}
        {!isCandidate && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Today */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-sm font-medium text-gray-500 mb-4">
                {t("instructors.stats.today")}
              </h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">
                    {t("instructors.stats.male")}
                  </span>
                  <div className="flex items-center space-x-2">
                    <span className="font-semibold text-green-600">
                      {stats.today.male}
                    </span>
                    {stats.today.total > 0 && (
                      <span className="text-xs text-gray-500">
                        (
                        {((stats.today.male / stats.today.total) * 100).toFixed(
                          1
                        )}
                        %)
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">
                    {t("instructors.stats.female")}
                  </span>
                  <div className="flex items-center space-x-2">
                    <span className="font-semibold text-red-600">
                      {stats.today.female}
                    </span>
                    {stats.today.total > 0 && (
                      <span className="text-xs text-gray-500">
                        (
                        {(
                          (stats.today.female / stats.today.total) *
                          100
                        ).toFixed(1)}
                        %)
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex justify-between pt-2 border-t border-gray-200">
                  <span className="text-sm font-medium text-gray-900">
                    {t("instructors.stats.total")}
                  </span>
                  <span className="font-bold text-gray-900">
                    {stats.today.total}
                  </span>
                </div>
              </div>
            </div>

            {/* This Month */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-sm font-medium text-gray-500 mb-4">
                {selectedYear !== "all" && selectedMonth !== "all"
                  ? `${t(
                      `instructors.stats.months.${selectedMonth}`
                    )} ${selectedYear}`
                  : t("instructors.stats.thisMonth")}
              </h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">
                    {t("instructors.stats.male")}
                  </span>
                  <div className="flex items-center space-x-2">
                    <span className="font-semibold text-green-600">
                      {stats.month.male}
                    </span>
                    {stats.month.total > 0 && (
                      <span className="text-xs text-gray-500">
                        (
                        {((stats.month.male / stats.month.total) * 100).toFixed(
                          1
                        )}
                        %)
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">
                    {t("instructors.stats.female")}
                  </span>
                  <div className="flex items-center space-x-2">
                    <span className="font-semibold text-red-600">
                      {stats.month.female}
                    </span>
                    {stats.month.total > 0 && (
                      <span className="text-xs text-gray-500">
                        (
                        {(
                          (stats.month.female / stats.month.total) *
                          100
                        ).toFixed(1)}
                        %)
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex justify-between pt-2 border-t border-gray-200">
                  <span className="text-sm font-medium text-gray-900">
                    {t("instructors.stats.total")}
                  </span>
                  <span className="font-bold text-gray-900">
                    {stats.month.total}
                  </span>
                </div>
              </div>
            </div>

            {/* This Year / Selected Year */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-sm font-medium text-gray-500 mb-4">
                {selectedYear === "all"
                  ? t("instructors.stats.thisYear")
                  : selectedYear}
              </h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">
                    {t("instructors.stats.male")}
                  </span>
                  <div className="flex items-center space-x-2">
                    <span className="font-semibold text-green-600">
                      {stats.year.male}
                    </span>
                    {stats.year.total > 0 && (
                      <span className="text-xs text-gray-500">
                        (
                        {((stats.year.male / stats.year.total) * 100).toFixed(
                          1
                        )}
                        %)
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">
                    {t("instructors.stats.female")}
                  </span>
                  <div className="flex items-center space-x-2">
                    <span className="font-semibold text-red-600">
                      {stats.year.female}
                    </span>
                    {stats.year.total > 0 && (
                      <span className="text-xs text-gray-500">
                        (
                        {((stats.year.female / stats.year.total) * 100).toFixed(
                          1
                        )}
                        %)
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex justify-between pt-2 border-t border-gray-200">
                  <span className="text-sm font-medium text-gray-900">
                    {t("instructors.stats.total")}
                  </span>
                  <span className="font-bold text-gray-900">
                    {stats.year.total}
                  </span>
                </div>
              </div>
            </div>

            {/* All Time */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-sm font-medium text-gray-500 mb-4">
                {t("instructors.stats.allTime")}
              </h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">
                    {t("instructors.stats.male")}
                  </span>
                  <div className="flex items-center space-x-2">
                    <span className="font-semibold text-green-600">
                      {stats.allTime.male}
                    </span>
                    {stats.allTime.total > 0 && (
                      <span className="text-xs text-gray-500">
                        (
                        {(
                          (stats.allTime.male / stats.allTime.total) *
                          100
                        ).toFixed(1)}
                        %)
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">
                    {t("instructors.stats.female")}
                  </span>
                  <div className="flex items-center space-x-2">
                    <span className="font-semibold text-red-600">
                      {stats.allTime.female}
                    </span>
                    {stats.allTime.total > 0 && (
                      <span className="text-xs text-gray-500">
                        (
                        {(
                          (stats.allTime.female / stats.allTime.total) *
                          100
                        ).toFixed(1)}
                        %)
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex justify-between pt-2 border-t border-gray-200">
                  <span className="text-sm font-medium text-gray-900">
                    {t("instructors.stats.total")}
                  </span>
                  <span className="font-bold text-gray-900">
                    {stats.allTime.total}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Search and Filters - Hidden for candidates */}
        {!isCandidate && (
          <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3">
            {/* Search Bar Row */}
            <div className="relative">
              <input
                type="text"
                placeholder={t("instructors.search")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-3 pl-10 border-2 border-gray-400 rounded-lg bg-gray-50 font-semibold text-gray-900 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-black/25 focus:border-black text-base"
              />
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>

            {/* Filters Row */}
            <div className="flex flex-wrap items-center gap-2">
              {/* Sort By */}
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black text-sm bg-white"
              >
                <option value="name-az">{t("instructors.sort.nameAZ")}</option>
                <option value="name-za">{t("instructors.sort.nameZA")}</option>
                <option value="experience-high">
                  {t("instructors.sort.experienceHigh")}
                </option>
                <option value="experience-low">
                  {t("instructors.sort.experienceLow")}
                </option>
              </select>

              {/* Status Filter */}
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black text-sm bg-white"
              >
                <option value="">{t("instructors.filters.allStatuses")}</option>
                <option value="active">
                  {t("instructors.filters.active")}
                </option>
                <option value="inactive">
                  {t("instructors.filters.inactive")}
                </option>
              </select>

              {/* Instructor Type Filter */}
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black text-sm bg-white"
              >
                <option value="">{t("instructors.filters.allTypes")}</option>
                {uniqueInstructorTypes.map((type) => (
                  <option key={type} value={type}>
                    {translateInstructorType(type)}
                  </option>
                ))}
              </select>

              {/* Category Filter */}
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black text-sm bg-white"
              >
                <option value="">
                  {t("instructors.filters.allCategories")}
                </option>
                {uniqueCategories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>

              {/* Date From Filter */}
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                  {t("instructors.filters.dateFrom")}:
                </label>
                <input
                  type="date"
                  value={
                    filterDateFrom
                      ? formatDateForInput(
                          parseDateString(filterDateFrom) || filterDateFrom
                        )
                      : ""
                  }
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value) {
                      const date = new Date(value);
                      setFilterDateFrom(formatDate(date));
                    } else {
                      setFilterDateFrom("");
                    }
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black text-sm bg-white"
                />
              </div>

              {/* Date To Filter */}
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                  {t("instructors.filters.dateTo")}:
                </label>
                <input
                  type="date"
                  value={
                    filterDateTo
                      ? formatDateForInput(
                          parseDateString(filterDateTo) || filterDateTo
                        )
                      : ""
                  }
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value) {
                      const date = new Date(value);
                      // Validate that "to date" is not before "from date"
                      if (filterDateFrom) {
                        const fromDate = parseDateString(filterDateFrom);
                        if (fromDate && date < fromDate) {
                          return; // Don't update if invalid
                        }
                      }
                      setFilterDateTo(formatDate(date));
                    } else {
                      setFilterDateTo("");
                    }
                  }}
                  min={
                    filterDateFrom
                      ? formatDateForInput(
                          parseDateString(filterDateFrom) || filterDateFrom
                        )
                      : undefined
                  }
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black text-sm bg-white"
                />
              </div>

              {/* Clear Filters */}
              {(searchQuery ||
                filterStatus ||
                filterType ||
                filterCategory ||
                filterDateFrom ||
                filterDateTo ||
                sortBy !== "name-az") && (
                <button
                  onClick={handleClearFilters}
                  className="ml-auto px-3 py-2 text-sm text-gray-600 hover:text-black hover:bg-gray-100 rounded-lg transition-colors flex items-center space-x-1"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                  <span>{t("instructors.clearAll")}</span>
                </button>
              )}
            </div>
          </div>
        )}

        {/* Results Count - Hidden for candidates */}
        {!isCandidate && !loading && instructors.length > 0 && (
          <div className="flex items-center justify-between text-sm text-gray-600 px-2">
            <span>
              {t("instructors.showing")}{" "}
              <span className="font-semibold text-gray-900">
                {filteredAndSortedInstructors.length > 0
                  ? `${startIndex + 1}-${Math.min(
                      endIndex,
                      filteredAndSortedInstructors.length
                    )}`
                  : 0}
              </span>{" "}
              {t("instructors.of")}{" "}
              <span className="font-semibold text-gray-900">
                {filteredAndSortedInstructors.length}
              </span>{" "}
              {t("instructors.instructorsText")}
              {filteredAndSortedInstructors.length !== instructors.length && (
                <span className="text-gray-500">
                  {" "}
                  ({t("instructors.of")} {instructors.length}{" "}
                  {t("instructors.total")})
                </span>
              )}
            </span>
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {filteredAndSortedInstructors.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <svg
                className="w-16 h-16 mb-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
              <p>{t("instructors.noInstructors")}</p>
              {isAdmin && (
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="mt-4 text-black hover:underline"
                >
                  {t("instructors.addFirstInstructor")}
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    {!isCandidate && <th className="w-4 px-2 py-3"></th>}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("instructors.table.name")}
                    </th>
                    {isCandidate && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("instructors.table.surname")}
                      </th>
                    )}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("instructors.table.phoneNumber")}
                    </th>
                    {!isCandidate && (
                      <>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t("instructors.table.categoryLicenses")}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t("instructors.table.instructorType")}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t("instructors.table.experience")}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t("instructors.table.hours")}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t("instructors.table.candidates")}
                        </th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t("instructors.table.actions")}
                        </th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedInstructors.map((instructor) => (
                    <tr key={instructor.id} className="hover:bg-gray-50">
                      {!isCandidate && (
                        <td className="px-2 py-4">
                          <div
                            className={`w-3 h-3 rounded-full ${getStatusColor(
                              instructor.status
                            )}`}
                          ></div>
                        </td>
                      )}
                      <td className="px-6 py-4">
                        <div>
                          {isCandidate ? (
                            <div className="text-sm font-medium text-gray-900">
                              {instructor.firstName}
                            </div>
                          ) : (
                            <>
                              <div className="text-sm font-medium text-gray-900">
                                {instructor.firstName} {instructor.lastName}
                              </div>
                              <div className="text-sm text-gray-500">
                                @{instructor.username}
                              </div>
                            </>
                          )}
                        </div>
                      </td>
                      {isCandidate && (
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900">
                            {instructor.lastName}
                          </div>
                        </td>
                      )}
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-gray-900">
                            {getFullPhoneNumber(instructor)}
                          </span>
                          {isCandidate ? (
                            <>
                              <button
                                onClick={() => openWhatsApp(instructor)}
                                className="text-green-600 hover:text-green-700 transition-colors"
                                title={t("instructors.actions.whatsapp")}
                              >
                                <svg
                                  className="w-5 h-5"
                                  fill="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                                </svg>
                              </button>
                              <button
                                onClick={() => openViber(instructor)}
                                className="text-purple-600 hover:text-purple-700 transition-colors"
                                title={t("instructors.actions.viber")}
                              >
                                <svg
                                  className="w-5 h-5"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M13 4C12.4477 4 12 4.44772 12 5C12 5.55228 12.4477 6 13 6C14.2728 6 15.2557 6.41989 15.9179 7.08211C16.5801 7.74433 17 8.72725 17 10C17 10.5523 17.4477 11 18 11C18.5523 11 19 10.5523 19 10C19 8.27275 18.4199 6.75567 17.3321 5.66789C16.2443 4.58011 14.7272 4 13 4Z"
                                    fill="currentColor"
                                  />
                                  <path
                                    d="M5.014 8.00613C5.12827 7.1024 6.30277 5.87414 7.23488 6.01043L7.23339 6.00894C8.01251 6.15699 8.65217 7.32965 9.07373 8.10246C9.14298 8.22942 9.20635 8.34559 9.26349 8.44465C9.55041 8.95402 9.3641 9.4701 9.09655 9.68787C9.06561 9.7128 9.03317 9.73855 8.9998 9.76504C8.64376 10.0477 8.18114 10.4149 8.28943 10.7834C8.5 11.5 11 14 12.2296 14.7107C12.6061 14.9283 12.8988 14.5057 13.1495 14.1438C13.2087 14.0583 13.2656 13.9762 13.3207 13.9067C13.5301 13.6271 14.0466 13.46 14.5548 13.736C15.3138 14.178 16.0288 14.6917 16.69 15.27C17.0202 15.546 17.0977 15.9539 16.8689 16.385C16.4659 17.1443 15.3003 18.1456 14.4542 17.9421C12.9764 17.5868 7 15.27 5.08033 8.55801C4.97981 8.26236 4.99645 8.13792 5.01088 8.02991L5.014 8.00613Z"
                                    fill="currentColor"
                                  />
                                  <path
                                    d="M13 7C12.4477 7 12 7.44772 12 8C12 8.55228 12.4477 9 13 9C13.1748 9 13.4332 9.09745 13.6679 9.33211C13.9025 9.56676 14 9.82523 14 10C14 10.5523 14.4477 11 15 11C15.5523 11 16 10.5523 16 10C16 9.17477 15.5975 8.43324 15.0821 7.91789C14.5668 7.40255 13.8252 7 13 7Z"
                                    fill="currentColor"
                                  />
                                  <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M7.51742 23.8312C7.54587 23.8469 7.57508 23.8612 7.60492 23.874C8.14762 24.1074 8.81755 23.5863 10.1574 22.5442L11.5 21.5C14.1884 21.589 16.514 21.2362 18.312 20.6071C20.3227 19.9035 21.9036 18.3226 22.6072 16.3119C23.5768 13.541 23.5768 8.45883 22.6072 5.68794C21.9036 3.67722 20.3227 2.0963 18.312 1.39271C15.1103 0.272407 8.82999 0.293306 5.68806 1.39271C3.67733 2.0963 2.09642 3.67722 1.39283 5.68794C0.423255 8.45883 0.423255 13.541 1.39283 16.3119C2.09642 18.3226 3.67733 19.9035 5.68806 20.6071C6.08252 20.7451 6.52371 20.8965 7 21C7 22.6974 7 23.5461 7.51742 23.8312ZM9 20.9107V19.7909C9 19.5557 8.836 19.3524 8.60597 19.3032C7.84407 19.1403 7.08676 18.9776 6.34862 18.7193C4.91238 18.2168 3.78316 17.0875 3.2806 15.6513C2.89871 14.5599 2.66565 12.8453 2.66565 10.9999C2.66565 9.15453 2.89871 7.43987 3.2806 6.3485C3.78316 4.91227 4.91238 3.78304 6.34862 3.28048C7.61625 2.83692 9.71713 2.56282 11.9798 2.56032C14.2422 2.55782 16.3561 2.82723 17.6514 3.28048C19.0876 3.78304 20.2169 4.91227 20.7194 6.3485C21.1013 7.43987 21.3344 9.15453 21.3344 10.9999C21.3344 12.8453 21.1013 14.5599 20.7194 15.6513C20.2169 17.0875 19.0876 18.2168 17.6514 18.7193C15.5197 19.4652 13.259 19.549 11.0239 19.4828C10.9071 19.4794 10.7926 19.5165 10.7004 19.5882L9 20.9107Z"
                                    fill="currentColor"
                                  />
                                </svg>
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => openWhatsApp(instructor)}
                                className="text-green-600 hover:text-green-700 transition-colors"
                                title={t("instructors.actions.whatsapp")}
                              >
                                <svg
                                  className="w-5 h-5"
                                  fill="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                                </svg>
                              </button>
                              <button
                                onClick={() => openViber(instructor)}
                                className="text-purple-600 hover:text-purple-700 transition-colors"
                                title={t("instructors.actions.viber")}
                              >
                                <svg
                                  className="w-5 h-5"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M13 4C12.4477 4 12 4.44772 12 5C12 5.55228 12.4477 6 13 6C14.2728 6 15.2557 6.41989 15.9179 7.08211C16.5801 7.74433 17 8.72725 17 10C17 10.5523 17.4477 11 18 11C18.5523 11 19 10.5523 19 10C19 8.27275 18.4199 6.75567 17.3321 5.66789C16.2443 4.58011 14.7272 4 13 4Z"
                                    fill="currentColor"
                                  />
                                  <path
                                    d="M5.014 8.00613C5.12827 7.1024 6.30277 5.87414 7.23488 6.01043L7.23339 6.00894C8.01251 6.15699 8.65217 7.32965 9.07373 8.10246C9.14298 8.22942 9.20635 8.34559 9.26349 8.44465C9.55041 8.95402 9.3641 9.4701 9.09655 9.68787C9.06561 9.7128 9.03317 9.73855 8.9998 9.76504C8.64376 10.0477 8.18114 10.4149 8.28943 10.7834C8.5 11.5 11 14 12.2296 14.7107C12.6061 14.9283 12.8988 14.5057 13.1495 14.1438C13.2087 14.0583 13.2656 13.9762 13.3207 13.9067C13.5301 13.6271 14.0466 13.46 14.5548 13.736C15.3138 14.178 16.0288 14.6917 16.69 15.27C17.0202 15.546 17.0977 15.9539 16.8689 16.385C16.4659 17.1443 15.3003 18.1456 14.4542 17.9421C12.9764 17.5868 7 15.27 5.08033 8.55801C4.97981 8.26236 4.99645 8.13792 5.01088 8.02991L5.014 8.00613Z"
                                    fill="currentColor"
                                  />
                                  <path
                                    d="M13 7C12.4477 7 12 7.44772 12 8C12 8.55228 12.4477 9 13 9C13.1748 9 13.4332 9.09745 13.6679 9.33211C13.9025 9.56676 14 9.82523 14 10C14 10.5523 14.4477 11 15 11C15.5523 11 16 10.5523 16 10C16 9.17477 15.5975 8.43324 15.0821 7.91789C14.5668 7.40255 13.8252 7 13 7Z"
                                    fill="currentColor"
                                  />
                                  <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M7.51742 23.8312C7.54587 23.8469 7.57508 23.8612 7.60492 23.874C8.14762 24.1074 8.81755 23.5863 10.1574 22.5442L11.5 21.5C14.1884 21.589 16.514 21.2362 18.312 20.6071C20.3227 19.9035 21.9036 18.3226 22.6072 16.3119C23.5768 13.541 23.5768 8.45883 22.6072 5.68794C21.9036 3.67722 20.3227 2.0963 18.312 1.39271C15.1103 0.272407 8.82999 0.293306 5.68806 1.39271C3.67733 2.0963 2.09642 3.67722 1.39283 5.68794C0.423255 8.45883 0.423255 13.541 1.39283 16.3119C2.09642 18.3226 3.67733 19.9035 5.68806 20.6071C6.08252 20.7451 6.52371 20.8965 7 21C7 22.6974 7 23.5461 7.51742 23.8312ZM9 20.9107V19.7909C9 19.5557 8.836 19.3524 8.60597 19.3032C7.84407 19.1403 7.08676 18.9776 6.34862 18.7193C4.91238 18.2168 3.78316 17.0875 3.2806 15.6513C2.89871 14.5599 2.66565 12.8453 2.66565 10.9999C2.66565 9.15453 2.89871 7.43987 3.2806 6.3485C3.78316 4.91227 4.91238 3.78304 6.34862 3.28048C7.61625 2.83692 9.71713 2.56282 11.9798 2.56032C14.2422 2.55782 16.3561 2.82723 17.6514 3.28048C19.0876 3.78304 20.2169 4.91227 20.7194 6.3485C21.1013 7.43987 21.3344 9.15453 21.3344 10.9999C21.3344 12.8453 21.1013 14.5599 20.7194 15.6513C20.2169 17.0875 19.0876 18.2168 17.6514 18.7193C15.5197 19.4652 13.259 19.549 11.0239 19.4828C10.9071 19.4794 10.7926 19.5165 10.7004 19.5882L9 20.9107Z"
                                    fill="currentColor"
                                  />
                                </svg>
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                      {!isCandidate && (
                        <>
                          <td className="px-6 py-4">
                            <div className="flex flex-wrap gap-1">
                              {instructor.categoryLicenses?.map((license) => (
                                <span
                                  key={license}
                                  className="px-2 py-1 text-xs font-semibold bg-blue-100 text-blue-800 rounded"
                                >
                                  {license}
                                </span>
                              ))}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {instructor.instructorType ? (
                              translateInstructorType(instructor.instructorType)
                            ) : (
                              <span className="text-gray-400 italic">
                                {t("instructors.table.notSpecified")}
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {instructor.experience}
                          </td>
                          <td className="px-6 py-4">
                            <div className="space-y-1">
                              {/* Regular hours on top */}
                              <div>
                                <span className="font-medium text-sm text-gray-900">
                                  {formatHours(
                                    getInstructorCompletedRegularHours(
                                      instructor.id
                                    )
                                  )}
                                </span>
                                <span className="text-gray-500 text-sm">
                                  {" "}
                                  /{" "}
                                </span>
                                <span className="text-sm text-gray-900">
                                  {formatHours(
                                    getInstructorRegularHours(instructor.id)
                                  )}
                                </span>
                              </div>
                              {/* Bonus hours below */}
                              {getInstructorBonusHours(instructor.id) > 0 && (
                                <div className="text-xs text-gray-600">
                                  <span className="font-medium">
                                    {formatHours(
                                      getInstructorCompletedBonusHours(
                                        instructor.id
                                      )
                                    )}
                                  </span>
                                  <span className="text-gray-500"> / </span>
                                  <span>
                                    {formatHours(
                                      getInstructorBonusHours(instructor.id)
                                    )}
                                  </span>
                                  <span className="text-gray-500 ml-1">
                                    {t("instructors.table.bonus")}
                                  </span>
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex flex-col gap-1">
                              <div className="text-sm text-gray-900">
                                <span className="font-medium">
                                  {getActiveCandidatesCount(instructor.id)}
                                </span>{" "}
                                <span className="text-gray-500">
                                  {t("instructors.table.activeCandidates")}
                                </span>
                              </div>
                              <div className="text-sm text-gray-900">
                                <span className="font-medium">
                                  {getCompletedCandidatesCount(instructor.id)}
                                </span>{" "}
                                <span className="text-gray-500">
                                  {t("instructors.table.completedCandidates")}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <div className="flex items-center justify-center space-x-3">
                              <button
                                onClick={() => handleView(instructor)}
                                className="text-blue-600 hover:text-blue-900 transition-colors"
                                title={t("instructors.actions.view")}
                              >
                                <svg
                                  className="w-5 h-5"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                  />
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                  />
                                </svg>
                              </button>
                              {isAdmin && (
                                <>
                                  <button
                                    onClick={() => handleEdit(instructor)}
                                    className="text-indigo-600 hover:text-indigo-900 transition-colors"
                                    title={t("instructors.actions.edit")}
                                  >
                                    <svg
                                      className="w-5 h-5"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                      />
                                    </svg>
                                  </button>
                                  <button
                                    onClick={() => {
                                      setInstructorToDelete(instructor);
                                      setIsDeleteModalOpen(true);
                                    }}
                                    className="text-red-600 hover:text-red-900 transition-colors"
                                    title={t("instructors.actions.delete")}
                                  >
                                    <svg
                                      className="w-5 h-5"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                      />
                                    </svg>
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {!loading && filteredAndSortedInstructors.length > itemsPerPage && (
          <div className="flex items-center justify-center space-x-2 mt-4">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {t("common.previous")}
            </button>

            <div className="flex items-center space-x-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-3 py-2 border rounded-lg text-sm font-medium transition-colors ${
                      currentPage === pageNum
                        ? "bg-black text-white border-black"
                        : "border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(totalPages, prev + 1))
              }
              disabled={currentPage === totalPages}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {t("common.next")}
            </button>
          </div>
        )}
      </div>

      {/* Add/Edit Instructor Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-gray-200 flex-shrink-0">
              <h3 className="text-xl font-bold text-gray-900">
                {editingInstructor
                  ? t("instructors.editInstructor")
                  : t("instructors.addInstructor")}
              </h3>
            </div>

            <form
              onSubmit={handleSubmit}
              className="flex flex-col flex-1 overflow-hidden"
            >
              <div className="p-6 overflow-y-auto flex-1">
                <div className="space-y-6">
                  {/* Personal Information */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">
                      {t("instructors.form.personalInfo")}
                    </h4>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {/* First Name */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t("instructors.form.firstName")}{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 ${
                            touched.firstName && errors.firstName
                              ? "border-red-500"
                              : "border-gray-300"
                          }`}
                        />
                        {touched.firstName && errors.firstName && (
                          <p className="mt-1 text-xs text-red-500">
                            {errors.firstName}
                          </p>
                        )}
                      </div>

                      {/* Last Name */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t("instructors.form.lastName")}{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 ${
                            touched.lastName && errors.lastName
                              ? "border-red-500"
                              : "border-gray-300"
                          }`}
                        />
                        {touched.lastName && errors.lastName && (
                          <p className="mt-1 text-xs text-red-500">
                            {errors.lastName}
                          </p>
                        )}
                      </div>

                      {/* Phone Prefix and Number */}
                      <div className="grid grid-cols-3 gap-3 items-start">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t("instructors.form.phonePrefix")}{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="tel"
                            name="phonePrefix"
                            value={formData.phonePrefix}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder="+383"
                            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 ${
                              touched.phonePrefix && errors.phonePrefix
                                ? "border-red-500"
                                : "border-gray-300"
                            }`}
                          />
                          {touched.phonePrefix && errors.phonePrefix && (
                            <p className="mt-1 text-xs text-red-500">
                              {errors.phonePrefix}
                            </p>
                          )}
                        </div>
                        <div className="col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t("instructors.form.phoneNumber")}{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="tel"
                            name="phoneNumber"
                            value={formData.phoneNumber}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder={t(
                              "instructors.form.phoneNumberPlaceholder"
                            )}
                            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 ${
                              touched.phoneNumber && errors.phoneNumber
                                ? "border-red-500"
                                : "border-gray-300"
                            }`}
                          />
                          {touched.phoneNumber && errors.phoneNumber && (
                            <p className="mt-1 text-xs text-red-500">
                              {errors.phoneNumber}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Email */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t("instructors.form.email")}
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 ${
                            touched.email && errors.email
                              ? "border-red-500"
                              : "border-gray-300"
                          }`}
                        />
                        {touched.email && errors.email && (
                          <p className="mt-1 text-xs text-red-500">
                            {errors.email}
                          </p>
                        )}
                      </div>

                      {/* Username */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t("instructors.form.username")}{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="username"
                          value={formData.username}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 ${
                            touched.username && errors.username
                              ? "border-red-500"
                              : "border-gray-300"
                          }`}
                        />
                        {touched.username && errors.username && (
                          <p className="mt-1 text-xs text-red-500">
                            {errors.username}
                          </p>
                        )}
                      </div>

                      {/* Personal Number */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t("instructors.form.personalNumber")}{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="personalNumber"
                          value={formData.personalNumber}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 ${
                            touched.personalNumber && errors.personalNumber
                              ? "border-red-500"
                              : "border-gray-300"
                          }`}
                        />
                        {touched.personalNumber && errors.personalNumber && (
                          <p className="mt-1 text-xs text-red-500">
                            {errors.personalNumber}
                          </p>
                        )}
                      </div>

                      {/* Password */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t("instructors.form.password")}{" "}
                          {(!editingInstructor || !editingInstructor.authUid) && (
                            <span className="text-red-500">*</span>
                          )}
                          {editingInstructor && editingInstructor.authUid && (
                            <span className="text-gray-400 text-xs ml-1">
                              ({t("instructors.form.passwordOptional")})
                            </span>
                          )}
                        </label>
                        {editingInstructor && !editingInstructor.authUid && (
                          <p className="text-xs text-amber-600 mb-1">
                            {t("instructors.form.passwordRequiredForAuth")}
                          </p>
                        )}
                        <input
                          type="password"
                          name="password"
                          value={formData.password}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder={t(
                            "instructors.form.passwordPlaceholder"
                          )}
                          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 ${
                            touched.password && errors.password
                              ? "border-red-500"
                              : "border-gray-300"
                          }`}
                        />
                        {touched.password && errors.password && (
                          <p className="mt-1 text-xs text-red-500">
                            {errors.password}
                          </p>
                        )}
                      </div>

                      {/* Birthday */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t("instructors.form.birthday")}{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="date"
                          name="birthday"
                          value={formData.birthday}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 ${
                            touched.birthday && errors.birthday
                              ? "border-red-500"
                              : "border-gray-300"
                          }`}
                        />
                        {touched.birthday && errors.birthday && (
                          <p className="mt-1 text-xs text-red-500">
                            {errors.birthday}
                          </p>
                        )}
                      </div>

                      {/* Gender */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t("instructors.form.gender")}{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <select
                          name="gender"
                          value={formData.gender}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 ${
                            touched.gender && errors.gender
                              ? "border-red-500"
                              : "border-gray-300"
                          }`}
                        >
                          <option value="">
                            {t("instructors.form.selectGender")}
                          </option>
                          <option value="male">
                            {t("instructors.form.male")}
                          </option>
                          <option value="female">
                            {t("instructors.form.female")}
                          </option>
                        </select>
                        {touched.gender && errors.gender && (
                          <p className="mt-1 text-xs text-red-500">
                            {errors.gender}
                          </p>
                        )}
                      </div>

                      {/* Address */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t("instructors.form.address")}
                        </label>
                        <input
                          type="text"
                          name="address"
                          value={formData.address}
                          onChange={handleChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Professional Information */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">
                      {t("instructors.form.professionalInfo")}
                    </h4>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {/* Experience */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t("instructors.form.experience")}{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="number"
                          name="experience"
                          value={formData.experience}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          min="0"
                          placeholder={t(
                            "instructors.form.experiencePlaceholder"
                          )}
                          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 ${
                            touched.experience && errors.experience
                              ? "border-red-500"
                              : "border-gray-300"
                          }`}
                        />
                        {touched.experience && errors.experience && (
                          <p className="mt-1 text-xs text-red-500">
                            {errors.experience}
                          </p>
                        )}
                      </div>

                      {/* Instructor Type */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t("instructors.form.instructorType")}
                        </label>
                        <select
                          name="instructorType"
                          value={formData.instructorType}
                          onChange={handleChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10"
                        >
                          <option value="">
                            {t("instructors.form.selectType")}
                          </option>
                          <option value="Theory Instructor">
                            {t("instructors.form.theoryInstructor")}
                          </option>
                          <option value="Practical Instructor">
                            {t("instructors.form.practicalInstructor")}
                          </option>
                          <option value="Both">
                            {t("instructors.form.both")}
                          </option>
                        </select>
                      </div>

                      {/* Vehicle Type */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t("instructors.form.vehicleType")}
                        </label>
                        <select
                          name="vehicleType"
                          value={formData.vehicleType}
                          onChange={handleChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10"
                        >
                          <option value="">
                            {t("instructors.form.selectVehicleType")}
                          </option>
                          <option value="Own">
                            {t("instructors.form.ownVehicle")}
                          </option>
                          <option value="School">
                            {t("instructors.form.schoolVehicle")}
                          </option>
                        </select>
                      </div>

                      {/* Status */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t("instructors.form.status")}{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <select
                          name="status"
                          value={formData.status}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 ${
                            touched.status && errors.status
                              ? "border-red-500"
                              : "border-gray-300"
                          }`}
                        >
                          <option value="active">
                            {t("instructors.filters.active")}
                          </option>
                          <option value="inactive">
                            {t("instructors.filters.inactive")}
                          </option>
                        </select>
                        {touched.status && errors.status && (
                          <p className="mt-1 text-xs text-red-500">
                            {errors.status}
                          </p>
                        )}
                      </div>

                      {/* Category Licenses */}
                      <div className="lg:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t("instructors.form.categoryLicenses")}{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <div className="grid grid-cols-4 sm:grid-cols-6 gap-3">
                          {[
                            "AM",
                            "A1",
                            "A2",
                            "A",
                            "B1",
                            "B",
                            "BE",
                            "C1",
                            "C",
                            "CE",
                            "D1",
                            "D",
                            "DE",
                          ].map((category) => (
                            <label
                              key={category}
                              className="flex items-center space-x-2 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                name="categoryLicenses"
                                value={category}
                                checked={formData.categoryLicenses?.includes(
                                  category
                                )}
                                onChange={handleChange}
                                className="w-4 h-4 text-black border-gray-300 rounded focus:ring-black"
                              />
                              <span className="text-sm text-gray-700">
                                {category}
                              </span>
                            </label>
                          ))}
                        </div>
                        {touched.categoryLicenses &&
                          errors.categoryLicenses && (
                            <p className="mt-1 text-xs text-red-500">
                              {errors.categoryLicenses}
                            </p>
                          )}
                      </div>

                      {/* License Expiry Date */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t("instructors.form.licenseExpiryDate")}
                        </label>
                        <input
                          type="date"
                          name="licenseExpiryDate"
                          value={formData.licenseExpiryDate}
                          onChange={handleChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10"
                        />
                      </div>

                      {/* Health Certificate Expiry Date */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t("instructors.form.healthCertificateExpiryDate")}
                        </label>
                        <input
                          type="date"
                          name="healthCertificateExpiryDate"
                          value={formData.healthCertificateExpiryDate}
                          onChange={handleChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10"
                        />
                      </div>

                      {/* Notes */}
                      <div className="lg:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t("instructors.form.notes")}
                        </label>
                        <textarea
                          name="notes"
                          value={formData.notes}
                          onChange={handleChange}
                          rows={3}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Actions */}
              <div className="p-6 border-t border-gray-200 flex-shrink-0">
                <div className="flex flex-col sm:flex-row justify-end gap-3">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                  >
                    {t("instructors.form.cancel")}
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors font-medium"
                  >
                    {editingInstructor
                      ? t("instructors.form.updateInstructor")
                      : t("instructors.form.createInstructor")}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Instructor Modal */}
      {isViewModalOpen && viewingInstructor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center flex-shrink-0">
              <h3 className="text-xl font-bold text-gray-900">
                {t("instructors.view.title")}
              </h3>
              <button
                onClick={() => setIsViewModalOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="p-6 space-y-6 overflow-y-auto flex-1">
              {/* Personal Information */}
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-3">
                  {t("instructors.form.personalInfo")}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">
                      {t("instructors.view.fullName")}
                    </p>
                    <p className="font-medium">
                      {viewingInstructor.firstName} {viewingInstructor.lastName}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">
                      {t("instructors.view.username")}
                    </p>
                    <p className="font-medium">{viewingInstructor.username}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">
                      {t("instructors.view.phoneNumber")}
                    </p>
                    <p className="font-medium">
                      <div className="flex items-center space-x-2">
                        <span>{viewingInstructor.phoneNumber}</span>
                        <button
                          onClick={() => openWhatsApp(viewingInstructor)}
                          className="text-green-600 hover:text-green-700 transition-colors"
                          title={t("instructors.actions.whatsapp")}
                        >
                          <svg
                            className="w-5 h-5"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => openViber(viewingInstructor)}
                          className="text-purple-600 hover:text-purple-700 transition-colors"
                          title={t("instructors.actions.viber")}
                        >
                          <svg
                            className="w-5 h-5"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M13 4C12.4477 4 12 4.44772 12 5C12 5.55228 12.4477 6 13 6C14.2728 6 15.2557 6.41989 15.9179 7.08211C16.5801 7.74433 17 8.72725 17 10C17 10.5523 17.4477 11 18 11C18.5523 11 19 10.5523 19 10C19 8.27275 18.4199 6.75567 17.3321 5.66789C16.2443 4.58011 14.7272 4 13 4Z"
                              fill="currentColor"
                            />
                            <path
                              d="M5.014 8.00613C5.12827 7.1024 6.30277 5.87414 7.23488 6.01043L7.23339 6.00894C8.01251 6.15699 8.65217 7.32965 9.07373 8.10246C9.14298 8.22942 9.20635 8.34559 9.26349 8.44465C9.55041 8.95402 9.3641 9.4701 9.09655 9.68787C9.06561 9.7128 9.03317 9.73855 8.9998 9.76504C8.64376 10.0477 8.18114 10.4149 8.28943 10.7834C8.5 11.5 11 14 12.2296 14.7107C12.6061 14.9283 12.8988 14.5057 13.1495 14.1438C13.2087 14.0583 13.2656 13.9762 13.3207 13.9067C13.5301 13.6271 14.0466 13.46 14.5548 13.736C15.3138 14.178 16.0288 14.6917 16.69 15.27C17.0202 15.546 17.0977 15.9539 16.8689 16.385C16.4659 17.1443 15.3003 18.1456 14.4542 17.9421C12.9764 17.5868 7 15.27 5.08033 8.55801C4.97981 8.26236 4.99645 8.13792 5.01088 8.02991L5.014 8.00613Z"
                              fill="currentColor"
                            />
                            <path
                              d="M13 7C12.4477 7 12 7.44772 12 8C12 8.55228 12.4477 9 13 9C13.1748 9 13.4332 9.09745 13.6679 9.33211C13.9025 9.56676 14 9.82523 14 10C14 10.5523 14.4477 11 15 11C15.5523 11 16 10.5523 16 10C16 9.17477 15.5975 8.43324 15.0821 7.91789C14.5668 7.40255 13.8252 7 13 7Z"
                              fill="currentColor"
                            />
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M7.51742 23.8312C7.54587 23.8469 7.57508 23.8612 7.60492 23.874C8.14762 24.1074 8.81755 23.5863 10.1574 22.5442L11.5 21.5C14.1884 21.589 16.514 21.2362 18.312 20.6071C20.3227 19.9035 21.9036 18.3226 22.6072 16.3119C23.5768 13.541 23.5768 8.45883 22.6072 5.68794C21.9036 3.67722 20.3227 2.0963 18.312 1.39271C15.1103 0.272407 8.82999 0.293306 5.68806 1.39271C3.67733 2.0963 2.09642 3.67722 1.39283 5.68794C0.423255 8.45883 0.423255 13.541 1.39283 16.3119C2.09642 18.3226 3.67733 19.9035 5.68806 20.6071C6.08252 20.7451 6.52371 20.8965 7 21C7 22.6974 7 23.5461 7.51742 23.8312ZM9 20.9107V19.7909C9 19.5557 8.836 19.3524 8.60597 19.3032C7.84407 19.1403 7.08676 18.9776 6.34862 18.7193C4.91238 18.2168 3.78316 17.0875 3.2806 15.6513C2.89871 14.5599 2.66565 12.8453 2.66565 10.9999C2.66565 9.15453 2.89871 7.43987 3.2806 6.3485C3.78316 4.91227 4.91238 3.78304 6.34862 3.28048C7.61625 2.83692 9.71713 2.56282 11.9798 2.56032C14.2422 2.55782 16.3561 2.82723 17.6514 3.28048C19.0876 3.78304 20.2169 4.91227 20.7194 6.3485C21.1013 7.43987 21.3344 9.15453 21.3344 10.9999C21.3344 12.8453 21.1013 14.5599 20.7194 15.6513C20.2169 17.0875 19.0876 18.2168 17.6514 18.7193C15.5197 19.4652 13.259 19.549 11.0239 19.4828C10.9071 19.4794 10.7926 19.5165 10.7004 19.5882L9 20.9107Z"
                              fill="currentColor"
                            />
                          </svg>
                        </button>
                      </div>
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">
                      {t("instructors.view.email")}
                    </p>
                    <p className="font-medium">
                      {viewingInstructor.email || t("instructors.view.na")}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">
                      {t("instructors.view.personalNumber")}
                    </p>
                    <p className="font-medium">
                      {viewingInstructor.personalNumber}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">
                      {t("instructors.view.birthday")}
                    </p>
                    <p className="font-medium">
                      {formatDate(viewingInstructor.birthday)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">
                      {t("instructors.view.gender")}
                    </p>
                    <p className="font-medium capitalize">
                      {viewingInstructor.gender}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">
                      {t("instructors.view.address")}
                    </p>
                    <p className="font-medium">
                      {viewingInstructor.address || t("instructors.view.na")}
                    </p>
                  </div>

                  {/* License Expiry Date */}
                  <div>
                    <p className="text-sm text-gray-500">
                      {t("instructors.view.licenseExpiryDate")}
                    </p>
                    <p className="font-medium">
                      {viewingInstructor.licenseExpiryDate
                        ? formatDate(viewingInstructor.licenseExpiryDate)
                        : t("instructors.view.na")}
                    </p>
                  </div>

                  {/* Health Certificate Expiry Date */}
                  <div>
                    <p className="text-sm text-gray-500">
                      {t("instructors.view.healthCertificateExpiryDate")}
                    </p>
                    <p className="font-medium">
                      {viewingInstructor.healthCertificateExpiryDate
                        ? formatDate(
                            viewingInstructor.healthCertificateExpiryDate
                          )
                        : t("instructors.view.na")}
                    </p>
                  </div>
                </div>
              </div>

              {/* Professional Information */}
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-3">
                  {t("instructors.form.professionalInfo")}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">
                      {t("instructors.view.experience")}
                    </p>
                    <p className="font-medium">
                      {viewingInstructor.experience}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">
                      {t("instructors.view.instructorType")}
                    </p>
                    <p className="font-medium">
                      {viewingInstructor.instructorType
                        ? translateInstructorType(
                            viewingInstructor.instructorType
                          )
                        : t("instructors.view.na")}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">
                      {t("instructors.view.vehicleType")}
                    </p>
                    <p className="font-medium">
                      {viewingInstructor.vehicleType ||
                        t("instructors.view.na")}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">
                      {t("instructors.view.status")}
                    </p>
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        viewingInstructor.status === "active"
                          ? "bg-green-100 text-green-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {t(`instructors.filters.${viewingInstructor.status}`)}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">
                      {t("instructors.view.activeCandidates")}
                    </p>
                    <p className="font-medium">
                      {getActiveCandidatesCount(viewingInstructor.id)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">
                      {t("instructors.view.completedCandidates")}
                    </p>
                    <p className="font-medium">
                      {getCompletedCandidatesCount(viewingInstructor.id)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">
                      {t("instructors.view.totalHours")}
                    </p>
                    <p className="font-medium">
                      {formatHours(
                        getInstructorTotalHours(viewingInstructor.id)
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">
                      {t("instructors.view.completedHours")}
                    </p>
                    <p className="font-medium">
                      {formatHours(
                        getInstructorCompletedHours(viewingInstructor.id)
                      )}
                    </p>
                  </div>
                  {getInstructorBonusHours(viewingInstructor.id) > 0 && (
                    <>
                      <div>
                        <p className="text-sm text-gray-500">
                          {t("instructors.view.bonusHours")}
                        </p>
                        <p className="font-medium">
                          {formatHours(
                            getInstructorBonusHours(viewingInstructor.id)
                          )}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">
                          {t("instructors.view.completedBonusHours")}
                        </p>
                        <p className="font-medium">
                          {formatHours(
                            getInstructorCompletedBonusHours(
                              viewingInstructor.id
                            )
                          )}
                        </p>
                      </div>
                    </>
                  )}
                  <div className="md:col-span-2">
                    <p className="text-sm text-gray-500">
                      {t("instructors.view.categoryLicenses")}
                    </p>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {viewingInstructor.categoryLicenses?.map((license) => (
                        <span
                          key={license}
                          className="px-2 py-1 text-xs font-semibold bg-blue-100 text-blue-800 rounded"
                        >
                          {license}
                        </span>
                      ))}
                    </div>
                  </div>
                  {viewingInstructor.notes && (
                    <div className="md:col-span-2">
                      <p className="text-sm text-gray-500">
                        {t("instructors.view.notes")}
                      </p>
                      <p className="font-medium">{viewingInstructor.notes}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end gap-3 flex-shrink-0">
              {isAdmin && (
                <button
                  onClick={() => {
                    setWeeklyReportYear(new Date().getFullYear());
                    setWeeklyReportWeek(getISOWeekNumber(new Date()));
                    setIsWeeklyReportModalOpen(true);
                  }}
                  className="px-6 py-2 bg-gray-100 text-gray-800 rounded-lg hover:bg-gray-200 transition-colors font-medium flex items-center space-x-2"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <span>{t("instructors.view.generateWeeklyReport")}</span>
                </button>
              )}
              <button
                onClick={() => setIsViewModalOpen(false)}
                className="px-6 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors font-medium"
              >
                {t("instructors.view.close")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Weekly Report Modal */}
      {isWeeklyReportModalOpen && viewingInstructor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              {t("instructors.weeklyReport.title")}
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              {viewingInstructor.firstName} {viewingInstructor.lastName}
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("instructors.weeklyReport.selectYear")}
                </label>
                <select
                  value={weeklyReportYear}
                  onChange={(e) => {
                    setWeeklyReportYear(Number(e.target.value));
                    setWeeklyReportWeek(1);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10"
                >
                  {Array.from(
                    { length: new Date().getFullYear() - 2019 + 1 },
                    (_, i) => new Date().getFullYear() - i
                  ).map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("instructors.weeklyReport.selectWeek")}
                </label>
                <select
                  value={weeklyReportWeek}
                  onChange={(e) =>
                    setWeeklyReportWeek(Number(e.target.value))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10"
                >
                  {Array.from(
                    { length: getWeeksInYear(weeklyReportYear) },
                    (_, i) => i + 1
                  ).map((w) => (
                    <option key={w} value={w}>
                      {t("reports.print.week") || "Week"} {w}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setIsWeeklyReportModalOpen(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
              >
                {t("instructors.view.close")}
              </button>
              <button
                onClick={handleGenerateWeeklyReport}
                disabled={weeklyReportLoading}
                className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 font-medium disabled:opacity-50 flex items-center space-x-2"
              >
                {weeklyReportLoading ? (
                  <>
                    <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    <span>...</span>
                  </>
                ) : (
                  <span>{t("instructors.weeklyReport.generate")}</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && instructorToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              {t("instructors.delete.title")}
            </h3>
            <p className="text-gray-600 mb-6">
              {t("instructors.delete.message")}{" "}
              <span className="font-semibold text-gray-900">
                {instructorToDelete.firstName} {instructorToDelete.lastName}
              </span>
              {t("instructors.delete.cannotUndo")}
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setIsDeleteModalOpen(false);
                  setInstructorToDelete(null);
                }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                {t("instructors.delete.cancel")}
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
              >
                {t("instructors.delete.delete")}
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

export default Instructors;
